// FASE 5 — On-chain $REBEL widget. Read balance + convert virtual→on-chain.
// P1 — Web3Modal-lite: auto-detect MetaMask / Coinbase Wallet via EIP-1193
// (window.ethereum). Single wallet → 1-click connect. Multiple injected
// providers → small chooser. No new npm deps, no CDN — fully isolated.
import React, { useCallback, useEffect, useState } from "react";
import { Coins, Loader2, ExternalLink, AlertCircle, ArrowRightLeft, Wallet, X, Network } from "lucide-react";
import { API, safeJson } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../i18n/LanguageContext";
import { tFases } from "../../i18n/shadow-fases";

const BASE_CHAIN_ID_HEX = "0x2105"; // 8453 (Base mainnet)
const BASE_CHAIN_PARAMS = {
  chainId: BASE_CHAIN_ID_HEX,
  chainName: "Base",
  nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
  rpcUrls: ["https://mainnet.base.org"],
  blockExplorerUrls: ["https://basescan.org"],
};

// Detect all injected providers. EIP-6963 multi-provider discovery is widely
// supported by modern MetaMask/Coinbase; we fall back to window.ethereum
// (and its .providers array) for older builds.
function detectProviders() {
  if (typeof window === "undefined") return [];
  const out = [];
  // EIP-1193 single
  const eth = window.ethereum;
  if (eth) {
    // Some wallets expose .providers (array of EIP-1193 instances).
    if (Array.isArray(eth.providers) && eth.providers.length) {
      for (const p of eth.providers) {
        out.push({
          provider: p,
          name: p.isCoinbaseWallet ? "Coinbase Wallet" : p.isMetaMask ? "MetaMask" : "Injected",
          icon: p.isCoinbaseWallet ? "🟦" : p.isMetaMask ? "🦊" : "🔌",
        });
      }
    } else {
      out.push({
        provider: eth,
        name: eth.isCoinbaseWallet ? "Coinbase Wallet" : eth.isMetaMask ? "MetaMask" : "Injected",
        icon: eth.isCoinbaseWallet ? "🟦" : eth.isMetaMask ? "🦊" : "🔌",
      });
    }
  }
  return out;
}

export const OnchainConvertCard = ({ virtualBalance, onConverted }) => {
  const { token } = useAuth();
  const { lang } = useLanguage();
  const [wallet, setWallet] = useState(localStorage.getItem("rebel_wallet") || "");
  const [onchain, setOnchain] = useState(null);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [last, setLast] = useState(null);

  // Wallet connect state
  const [provider, setProvider] = useState(null);
  const [providerName, setProviderName] = useState(null);
  const [chainId, setChainId] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [chooser, setChooser] = useState(false);
  const listenersRef = React.useRef(null);

  const fetchOnchain = useCallback(async () => {
    if (!wallet || wallet.length !== 42) return;
    try {
      const r = await fetch(`${API}/shadow/onchain/balance/${wallet}`);
      const d = await safeJson(r);
      if (r.ok) setOnchain(d);
    } catch (_e) { /* non-critical */ }
  }, [wallet]);

  useEffect(() => { fetchOnchain(); }, [fetchOnchain]);

  const attachListeners = (p) => {
    // Detach any previous binding
    try {
      const prev = listenersRef.current;
      if (prev?.p) {
        prev.p.removeListener?.("accountsChanged", prev.onA);
        prev.p.removeListener?.("chainChanged", prev.onC);
      }
    } catch (_e) { /* noop */ }
    const onA = (accs) => {
      const a = (accs && accs[0]) || "";
      setWallet(a);
      if (a) localStorage.setItem("rebel_wallet", a);
    };
    const onC = (cid) => setChainId(cid);
    try {
      p.on?.("accountsChanged", onA);
      p.on?.("chainChanged", onC);
    } catch (_e) { /* noop */ }
    listenersRef.current = { p, onA, onC };
  };

  const connectWith = async (entry) => {
    setErr(null); setConnecting(true); setChooser(false);
    try {
      const p = entry.provider;
      const accs = await p.request({ method: "eth_requestAccounts" });
      const addr = (accs && accs[0]) || "";
      if (!addr) throw new Error(tFases(lang, "oc_failed"));
      const cid = await p.request({ method: "eth_chainId" });
      attachListeners(p);
      setProvider(p); setProviderName(entry.name); setChainId(cid);
      setWallet(addr); localStorage.setItem("rebel_wallet", addr);
    } catch (e) {
      setErr(e?.message || tFases(lang, "oc_failed"));
    } finally { setConnecting(false); }
  };

  const connect = () => {
    const list = detectProviders();
    if (list.length === 0) { setErr(tFases(lang, "oc_no_wallet")); return; }
    if (list.length === 1) { connectWith(list[0]); return; }
    setChooser(true);
  };

  const disconnect = () => {
    try {
      const prev = listenersRef.current;
      if (prev?.p) {
        prev.p.removeListener?.("accountsChanged", prev.onA);
        prev.p.removeListener?.("chainChanged", prev.onC);
      }
    } catch (_e) { /* noop */ }
    listenersRef.current = null;
    setProvider(null); setProviderName(null); setChainId(null);
    setWallet(""); localStorage.removeItem("rebel_wallet");
  };

  const switchToBase = async () => {
    if (!provider) return;
    setErr(null);
    try {
      await provider.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: BASE_CHAIN_ID_HEX }],
      });
    } catch (e) {
      // Chain not added yet → request it.
      if (e?.code === 4902 || /unrecognized/i.test(e?.message || "")) {
        try {
          await provider.request({
            method: "wallet_addEthereumChain",
            params: [BASE_CHAIN_PARAMS],
          });
        } catch (e2) { setErr(e2?.message || "switch failed"); }
      } else { setErr(e?.message || "switch failed"); }
    }
  };

  const convert = async () => {
    setErr(null); setLast(null);
    const n = parseInt(amount, 10);
    if (!n || n <= 0) { setErr(tFases(lang, "oc_invalid_amount")); return; }
    if (n > virtualBalance) { setErr(tFases(lang, "oc_insufficient")); return; }
    if (!/^0x[0-9a-fA-F]{40}$/.test(wallet)) { setErr(tFases(lang, "oc_invalid_wallet")); return; }
    localStorage.setItem("rebel_wallet", wallet);
    setBusy(true);
    try {
      const r = await fetch(`${API}/shadow/onchain/convert`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ amount: n, wallet_address: wallet }),
      });
      const d = await safeJson(r);
      if (!r.ok) throw new Error(d?.detail?.message || d?.detail || tFases(lang, "oc_failed"));
      setLast(d); setAmount("");
      await fetchOnchain();
      onConverted?.();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const wrongChain = !!provider && chainId && chainId.toLowerCase() !== BASE_CHAIN_ID_HEX;
  const connected = !!provider && !!wallet;

  return (
    <div className="mt-6 rounded-2xl border border-violet-400/30 bg-gradient-to-br from-violet-500/[0.06] to-fuchsia-500/[0.04] p-5 backdrop-blur" data-testid="onchain-convert-card">
      <div className="flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.2em] text-violet-200">
          <ArrowRightLeft className="h-4 w-4" /> {tFases(lang, "oc_title")}
        </h3>
        <a href="https://basescan.org/token/0xf9214b42c4c53791846E90066E52D7306adFACf5" target="_blank" rel="noreferrer" className="text-[10px] font-mono text-violet-300/80 hover:underline">
          {tFases(lang, "oc_contract_link")}
        </a>
      </div>
      <p className="mt-1 text-xs text-white/55">
        {tFases(lang, "oc_sub")}
      </p>

      {/* Wallet connect bar */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {!connected ? (
          <button
            type="button"
            onClick={connect}
            disabled={connecting}
            data-testid="onchain-connect-btn"
            className="inline-flex items-center gap-1.5 rounded-full border border-violet-400/40 bg-violet-400/[0.08] px-4 py-2 text-[11px] font-black uppercase tracking-[0.16em] text-violet-200 hover:bg-violet-400/[0.18] disabled:opacity-50"
          >
            {connecting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wallet className="h-3.5 w-3.5" />}
            {connecting ? tFases(lang, "oc_connecting") : tFases(lang, "oc_connect")}
          </button>
        ) : (
          <>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/40 bg-emerald-400/[0.08] px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.18em] text-emerald-200" data-testid="onchain-connected-badge">
              <Wallet className="h-3 w-3" /> {tFases(lang, "oc_connected")} · {providerName}
            </span>
            <button
              type="button"
              onClick={disconnect}
              data-testid="onchain-disconnect-btn"
              className="inline-flex items-center gap-1 rounded-full border border-white/15 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.18em] text-white/60 hover:text-rose-200 hover:border-rose-400/40"
            >
              <X className="h-3 w-3" /> {tFases(lang, "oc_disconnect")}
            </button>
            {wrongChain && (
              <button
                type="button"
                onClick={switchToBase}
                data-testid="onchain-switch-chain-btn"
                className="inline-flex items-center gap-1 rounded-full border border-amber-400/40 bg-amber-400/[0.08] px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.18em] text-amber-200 hover:bg-amber-400/[0.18]"
              >
                <Network className="h-3 w-3" /> {tFases(lang, "oc_switch_chain")}
              </button>
            )}
          </>
        )}
      </div>
      {wrongChain && (
        <p className="mt-2 text-[11px] text-amber-300/80" data-testid="onchain-wrong-chain">
          <AlertCircle className="inline h-3 w-3 mr-1" /> {tFases(lang, "oc_wrong_chain")}
        </p>
      )}

      {/* Wallet chooser modal — appears when multiple providers detected */}
      {chooser && (
        <div className="mt-3 rounded-xl border border-white/15 bg-black/60 p-3" data-testid="onchain-wallet-chooser">
          <p className="mb-2 text-[10px] font-mono uppercase tracking-[0.22em] text-white/50">{tFases(lang, "oc_choose_wallet")}</p>
          <div className="flex flex-wrap gap-2">
            {detectProviders().map((p, i) => (
              <button
                key={i}
                type="button"
                onClick={() => connectWith(p)}
                data-testid={`onchain-wallet-pick-${i}`}
                className="inline-flex items-center gap-2 rounded-full border border-violet-400/40 bg-violet-400/[0.06] px-4 py-2 text-xs font-bold text-violet-100 hover:bg-violet-400/[0.18]"
              >
                <span>{p.icon}</span> {p.name}
              </button>
            ))}
            <button type="button" onClick={() => setChooser(false)} className="inline-flex items-center gap-1 rounded-full border border-white/15 px-3 py-2 text-[11px] text-white/55">
              <X className="h-3 w-3" />
            </button>
          </div>
        </div>
      )}

      <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
        <div>
          <label className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/45">{tFases(lang, "oc_wallet")}</label>
          <input
            value={wallet}
            onChange={(e) => setWallet(e.target.value.trim())}
            placeholder="0x..."
            maxLength={42}
            readOnly={!!provider}
            data-testid="onchain-wallet-input"
            className={`mt-1.5 w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 font-mono text-xs outline-none focus:border-violet-400/50 ${provider ? "cursor-default opacity-75" : ""}`}
          />
          {onchain && (
            <p className="mt-1.5 font-mono text-[10px] text-white/55">
              {tFases(lang, "oc_balance")}: <span className="font-bold text-violet-200">{onchain.display_balance} {onchain.symbol}</span>
            </p>
          )}
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/45">{tFases(lang, "oc_amount")}</label>
          <div className="mt-1.5 flex gap-1">
            <input
              type="number" min="1" max={virtualBalance} value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0"
              data-testid="onchain-amount-input"
              className="flex-1 rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm outline-none focus:border-violet-400/50"
            />
            <button
              onClick={convert} disabled={busy || !amount || !wallet}
              data-testid="onchain-convert-btn"
              className="inline-flex items-center gap-1 rounded-lg bg-gradient-to-r from-violet-500 to-fuchsia-500 px-4 py-2 text-[11px] font-black uppercase tracking-wider text-white disabled:opacity-50"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Coins className="h-3.5 w-3.5" />}
              {tFases(lang, "oc_convert")}
            </button>
          </div>
        </div>
      </div>

      {err && (
        <div className="mt-3 rounded-xl border border-rose-500/40 bg-rose-500/[0.08] px-3 py-2 text-xs text-rose-200" data-testid="onchain-err">
          <AlertCircle className="inline h-3.5 w-3.5 mr-1" /> {err}
        </div>
      )}
      {last && (
        <div className="mt-3 rounded-xl border border-emerald-400/40 bg-emerald-400/[0.08] p-3 text-xs" data-testid="onchain-success">
          <p className="font-bold text-emerald-200">{tFases(lang, "oc_success", { n: last.amount })}</p>
          <a href={last.explorer_url} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 font-mono text-[11px] text-violet-300 hover:underline">
            {tFases(lang, "oc_view_tx")} <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      )}
    </div>
  );
};

export default OnchainConvertCard;
